/**
 * Drops unhandled exceptions thrown by scripts the host *in-app browser*
 * injects into our page, which are not our code and not actionable by us.
 *
 * Meta's in-app browser (Instagram / Facebook, both Android and iOS) injects a
 * telemetry script into every page it renders inside its WebView. That script
 * ships its measurements to the native shell over a JS-to-native bridge, via a
 * function it names `sendDataToNative`. When the WebView is torn down — the
 * visitor taps "done", backgrounds the app, or follows a link out — the native
 * side of the bridge goes away while the injected script is still posting, and
 * the bridge call throws:
 *
 * - Android: `Error invoking postMessage: Java object is gone`
 *   (the `@JavascriptInterface` object was garbage collected)
 * - iOS: `undefined is not an object (evaluating 'window.webkit.messageHandlers')`
 *
 * Those throws are unhandled, so `capture_exceptions: true` catches them off
 * `window.onerror` and PostHog files them against our project — with our URL,
 * our release, and no frame of ours anywhere in the stack. They alert as our
 * crashes even though no visitor-facing behaviour is affected.
 *
 * Inputs: a `before_send` event, already shaped by posthog-js.
 * Outputs: the same event, or `null` to drop it when the stack is entirely a
 * known injected bridge. Nothing else is touched.
 *
 * This deliberately matches on frame *function names* rather than on the error
 * message: the messages are browser-locale and platform specific, while the
 * bridge function names are stable and appear in no bundle of ours. Verified:
 * none of these names exist anywhere in this repository.
 */

/**
 * Frame names belonging to Meta's injected in-app browser bridge.
 *
 * `sendDataToNative` is the bridge entry point and appeared in every sample;
 * the other two are the callers that reach it (INP and pagehide reporting).
 */
const INJECTED_BRIDGE_FRAME_NAMES = new Set([
  'sendDataToNative',
  'sendINPMessage',
  'sendPageHideMessage',
])

/** One raw stack frame as posthog-js builds it client-side, before symbolication. */
interface RawStackFrame {
  function?: unknown
}

/** One `$exception_list` entry carrying the frames we inspect. */
interface ExceptionEntry {
  stacktrace?: { frames?: unknown } | null
}

/** Minimal `before_send` shape, so this module stays SDK-free and testable. */
export interface CapturedEvent {
  event: string
  properties?: { $exception_list?: unknown; [key: string]: unknown }
}

/** Reads a frame's function name, tolerating the `?` posthog-js uses for anonymous frames. */
function frameName(frame: unknown): string | null {
  if (!frame || typeof frame !== 'object') return null
  const name = (frame as RawStackFrame).function
  return typeof name === 'string' && name ? name : null
}

/**
 * True when any frame in `entry` names a known injected bridge function.
 *
 * Matching on *any* frame rather than the top one is deliberate: the throw
 * surfaces one frame above the bridge call on Android (inside `postMessage`)
 * and at the property access on iOS, so the bridge is never reliably on top.
 */
function hasInjectedBridgeFrame(entry: unknown): boolean {
  if (!entry || typeof entry !== 'object') return false
  const frames = (entry as ExceptionEntry).stacktrace?.frames
  if (!Array.isArray(frames)) return false
  return frames.some((frame) => {
    const name = frameName(frame)
    return name !== null && INJECTED_BRIDGE_FRAME_NAMES.has(name)
  })
}

/**
 * True when this event is an exception raised inside an injected in-app
 * browser bridge rather than inside our application code.
 */
export function isInjectedBridgeException(event: CapturedEvent): boolean {
  if (event.event !== '$exception') return false
  const list = event.properties?.$exception_list
  if (!Array.isArray(list) || list.length === 0) return false
  return list.some(hasInjectedBridgeFrame)
}

/**
 * `before_send` hook. Returns `null` for injected-bridge exceptions so they are
 * never sent, and passes every other event through untouched.
 *
 * A `null` in stays a `null` out — an earlier hook in the chain already decided
 * to drop the event.
 */
export function dropInjectedBridgeExceptions<T extends CapturedEvent>(event: T | null): T | null {
  if (!event) return event
  return isInjectedBridgeException(event) ? null : event
}
